import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { parseSource } from "@/lib/embed";

export default function KidsVideoPlayer({
  playlist,
  startIdx = 0,
  accentColor = "#FF6B00",
  profileId,
  sectionLabel,
  onClose,
  checkinOpen,
}) {
  const [idx, setIdx] = useState(startIdx);
  const [videoError, setVideoError] = useState(false);
  const [videoKey, setVideoKey] = useState(0);
  const [needsTap, setNeedsTap] = useState(false);
  const [isLandscape, setIsLandscape] = useState(
    () => window.matchMedia("(orientation: landscape)").matches
  );
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const media = playlist[idx];
  const source = media ? parseSource(media) : null;

  // Keep a stable ref to onClose so the keydown effect doesn't re-register
  // on every render when the parent passes a new arrow function.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Persist last-watched index
  useEffect(() => {
    if (sectionLabel && profileId) {
      localStorage.setItem(`mh_last_${profileId}_${sectionLabel}`, String(idx));
    }
  }, [idx, profileId, sectionLabel]);

  // Reset video error state when switching tracks
  useEffect(() => {
    setVideoError(false);
    setVideoKey((k) => k + 1);
  }, [idx]);

  // Pause/resume when check-in fires — handles both <video> and iframes
  useEffect(() => {
    if (source?.kind !== "direct") {
      const win = iframeRef.current?.contentWindow;
      if (!win) return;
      if (checkinOpen) {
        // YouTube postMessage pause
        win.postMessage(JSON.stringify({ event: "command", func: "pauseVideo", args: [] }), "*");
        // Vimeo postMessage pause
        win.postMessage(JSON.stringify({ method: "pause" }), "https://player.vimeo.com");
      } else {
        win.postMessage(JSON.stringify({ event: "command", func: "playVideo", args: [] }), "*");
        win.postMessage(JSON.stringify({ method: "play" }), "https://player.vimeo.com");
      }
      return;
    }
    const vid = videoRef.current;
    if (!vid) return;
    if (checkinOpen) {
      vid.pause();
      setNeedsTap(false);
    } else if (!vid.ended) {
      vid.play().catch(() => setNeedsTap(true));
    }
  }, [checkinOpen, source?.kind]);

  // Escape key to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onCloseRef.current?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Track orientation changes
  useEffect(() => {
    const mq = window.matchMedia("(orientation: landscape)");
    const handler = (e) => setIsLandscape(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const goPrev = () => setIdx((i) => Math.max(0, i - 1));
  const goNext = useCallback(() => {
    setIdx((i) => {
      if (i < playlist.length - 1) return i + 1;
      onClose?.();
      return i;
    });
  }, [playlist.length, onClose]);
  const handleEnded = goNext;

  const handleVideoError = () => setVideoError(true);
  const handleRetry = () => {
    setVideoError(false);
    setVideoKey((k) => k + 1);
  };

  if (!media || !source) return null;
  const isIframe = source.kind !== "direct";

  return (
    <AnimatePresence>
      <motion.div
        key="kids-player"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        onContextMenu={(e) => e.preventDefault()}
        className="fixed inset-0 flex flex-col"
        style={{ background: "#050507", zIndex: 60 }}
      >
        {/* Header — hidden in landscape (video takes full screen) */}
        {!isLandscape && (
          <header
            className="flex items-center justify-between px-4 shrink-0"
            style={{ borderBottom: "1px solid rgba(255,255,255,0.06)", minHeight: 56 }}
          >
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.3em] truncate" style={{ color: "rgba(255,255,255,0.35)" }}>
                {media.sectionLabel} · {idx + 1} / {playlist.length}
              </p>
              <h2
                className="text-sm font-semibold text-white truncate"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {media.title}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="ml-3 flex items-center gap-2 px-4 h-10 rounded-full text-white shrink-0"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)" }}
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
              <span style={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.05em" }}>Back</span>
            </button>
          </header>
        )}

        {/* Video surface */}
        <div className={`flex-1 min-h-0 overflow-hidden flex items-center justify-center relative${isLandscape ? "" : " px-2 py-4"}`}>
          {/* Floating back button in landscape */}
          {isLandscape && (
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-10 flex items-center gap-2 px-4 h-10 rounded-full text-white"
              style={{ background: "rgba(0,0,0,0.55)", border: "1px solid rgba(255,255,255,0.2)" }}
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
              <span style={{ fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.05em" }}>Back</span>
            </button>
          )}
          <motion.div
            key={idx}
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.25 }}
            className={`relative bg-black overflow-hidden${isLandscape ? " w-full h-full rounded-none" : " w-full max-w-6xl aspect-video max-h-full rounded-2xl"}`}
            style={{
              boxShadow: isLandscape ? "none" : `0 20px 60px -20px rgba(0,0,0,0.9), 0 0 40px -10px ${accentColor}55`,
            }}
          >
            {isIframe ? (
              <iframe
                ref={iframeRef}
                src={source.src}
                title={media.title}
                sandbox="allow-scripts allow-same-origin allow-presentation"
                referrerPolicy="strict-origin"
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                className="w-full h-full border-0 bg-black"
              />
            ) : (
              <>
                <video
                  key={videoKey}
                  ref={videoRef}
                  src={source.src}
                  poster={media.posterUrl || undefined}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  controlsList="nodownload noremoteplayback noplaybackrate"
                  disablePictureInPicture
                  onEnded={handleEnded}
                  onError={handleVideoError}
                  onContextMenu={(e) => e.preventDefault()}
                  className="w-full h-full object-contain bg-black"
                />
                {videoError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/90 gap-3">
                    <p className="text-white/50 text-sm">Failed to load video</p>
                    <button
                      type="button"
                      onClick={handleRetry}
                      className="text-xs text-white/40 hover:text-white underline transition-colors"
                    >
                      Retry
                    </button>
                  </div>
                )}
                {needsTap && !videoError && (
                  <button
                    type="button"
                    className="absolute inset-0 flex items-center justify-center bg-black/50"
                    onClick={() => {
                      videoRef.current?.play().catch(() => {});
                      setNeedsTap(false);
                    }}
                  >
                    <span style={{ fontSize: "1.1rem", fontWeight: 700, color: "#fff", background: "rgba(0,0,0,0.6)", borderRadius: "2rem", padding: "0.6rem 1.4rem" }}>
                      ▶ Tap to resume
                    </span>
                  </button>
                )}
              </>
            )}
            {isIframe && (
              <>
                <div className="absolute top-0 left-0 right-0 h-12 pointer-events-auto" aria-hidden
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} />
                <div className="absolute bottom-0 right-0 w-32 h-12 pointer-events-auto" aria-hidden
                  onClick={(e) => { e.preventDefault(); e.stopPropagation(); }} />
              </>
            )}
          </motion.div>
        </div>

        {/* Prev / Next nav — hidden in landscape */}
        {!isLandscape && <div className="flex items-center justify-center gap-4 pb-4 shrink-0">
          <button
            onClick={goPrev}
            disabled={idx === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold disabled:opacity-25"
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.12)",
              color: "#fff",
              fontFamily: "Nunito, Arial, sans-serif",
            }}
          >
            <ChevronLeft className="w-4 h-4" /> Prev
          </button>
          <button
            onClick={goNext}
            className="flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold"
            style={{
              background: accentColor,
              color: "#fff",
              fontFamily: "Nunito, Arial, sans-serif",
            }}
          >
            {idx < playlist.length - 1 ? "Next" : "Done"} <ChevronRight className="w-4 h-4" />
          </button>
        </div>}
      </motion.div>
    </AnimatePresence>
  );
}
